import {useEffect,useRef,useState} from 'react';

// Each browser client uses one Realtime channel per review even when the
// Feedback Inbox and Client Review dialog are open at the same time.
const rooms=new WeakMap();
export function reviewPeople(presenceState){
 const byId=new Map();
 for(const items of Object.values(presenceState||{})){
  for(const p of items||[]){
   if(!p||typeof p.name!=='string'||!['editor','reviewer'].includes(p.role))continue;
   const name=p.name.trim().slice(0,48),id=String(p.id||'').slice(0,80);
   if(name&&id)byId.set(id,{id,name,role:p.role});
  }
 }
 const editorSeen=new Set();
 return [...byId.values()].filter(p=>{
  if(p.role!=='editor')return true;
  if(editorSeen.has(p.name))return false;
  editorSeen.add(p.name);return true;
 }).sort((a,b)=>a.role===b.role?a.name.localeCompare(b.name):a.role==='editor'?-1:1);
}
export function reviewRoomTopic(key){
 return /^[a-f0-9]{64}$/.test(key||'')?'takefold-review-v1-'+key:null;
}
export async function guestReviewRoomKey(token){
 if(!/^[a-f0-9]{64}$/.test(token||''))return null;
 const digest=new Uint8Array(await crypto.subtle.digest('SHA-256',new TextEncoder().encode(token)));
 return [...digest].map(n=>n.toString(16).padStart(2,'0')).join('');
}
function getRoom(client,topic,name,role){
 let cache=rooms.get(client);
 if(!cache){cache=new Map();rooms.set(client,cache);}
 const prior=cache.get(topic);
 if(prior)return prior;
 const channel=client.channel(topic,{
  config:{presence:{key:crypto.randomUUID()},broadcast:{self:false,ack:false},private:false}
 });
 const room={channel,people:[],connected:false,listeners:new Set(),poll:null,notice:null,closed:false};
 cache.set(topic,room);
 const dispatch=()=>{for(const subscriber of room.listeners)subscriber.update(room.people,room.connected);};
 channel
  .on('presence',{event:'sync'},()=>{
   room.people=reviewPeople(channel.presenceState());dispatch();
  })
  .on('broadcast',{event:'takefold-changed'},()=>{
   if(room.notice)clearTimeout(room.notice);
   room.notice=setTimeout(()=>{
    room.notice=null;
    for(const subscriber of room.listeners)subscriber.changed();
   },240);
  })
  .subscribe(async status=>{
   if(room.closed)return;
   if(status==='SUBSCRIBED'){
    room.connected=true;
    dispatch();
    try{await channel.track({id:crypto.randomUUID(),name,role});}
    catch{room.connected=false;dispatch();}
   }else if(['CHANNEL_ERROR','TIMED_OUT','CLOSED'].includes(status)){
    room.connected=false;room.people=[];dispatch();
   }
  });
 room.poll=setInterval(()=>{
  if(document.visibilityState!=='hidden')for(const subscriber of room.listeners)subscriber.changed();
 },25000);
 return room;
}
export function useReviewRealtime(client,{roomKey,enabled=true,name,role,onChanged}){
 const [participants,setParticipants]=useState([]);
 const [connected,setConnected]=useState(false);
 const current=useRef(null),changed=useRef(onChanged);
 changed.current=onChanged;
 const topic=reviewRoomTopic(roomKey),label=String(name||'').trim().slice(0,48);
 useEffect(()=>{
  if(!client||!enabled||!topic||!label||!['editor','reviewer'].includes(role)){
   current.current=null;setParticipants([]);setConnected(false);return;
  }
  let alive=true;
  const room=getRoom(client,topic,label,role);
  current.current=room;
  const subscriber={
   update:(people,status)=>{if(alive){setParticipants(people);setConnected(status);}},
   changed:()=>{if(alive)changed.current?.();}
  };
  room.listeners.add(subscriber);
  subscriber.update(room.people,room.connected);
  return()=>{
   alive=false;
   room.listeners.delete(subscriber);
   if(current.current===room)current.current=null;
   if(room.listeners.size!==0)return;
   room.closed=true;
   clearInterval(room.poll);
   if(room.notice)clearTimeout(room.notice);
   const cache=rooms.get(client);cache?.delete(topic);
   if(!cache?.size)rooms.delete(client);
   room.channel.untrack().catch(()=>{});
   client.removeChannel(room.channel);
  };
 },[client,enabled,topic,label,role]);
 const notify=()=>{
  const room=current.current;
  if(!room||room.closed||!room.connected)return;
  room.channel.send({type:'broadcast',event:'takefold-changed',payload:{kind:'refresh'}}).catch(()=>{});
 };
 return {participants,connected,notify};
}
